import { useState, useCallback, useMemo, FC, ReactNode } from 'react';
import { DeviceFamily, Placement } from '@amzn/fire-tv-campaign-automation-service';
import { FeatureFlag, isFeatureEnabled } from 'src/featureFlags';
import { useSelection } from 'src/contexts/SelectionContext';

import { DevicePreviewNavigationContext } from './DevicePreviewNavigationContext';

interface DevicePreviewNavigationProviderProps {
  children: ReactNode;
}

/**
 * Provider component for the Device Preview Navigation context.
 * Manages the Test Campaign modal open/close state, computes device preview
 * availability, and provides stable callbacks via useMemo.
 */
export const DevicePreviewNavigationProvider: FC<DevicePreviewNavigationProviderProps> = ({
  children,
}) => {
  const [isTestCampaignModalOpen, setIsTestCampaignModalOpen] = useState(false);
  const { deviceFamily, placement } = useSelection();

  const openTestCampaignModal = useCallback(() => setIsTestCampaignModalOpen(true), []);
  const closeTestCampaignModal = useCallback(() => setIsTestCampaignModalOpen(false), []);

  const isDevicePreviewAvailable = useMemo(
    () =>
      isFeatureEnabled(FeatureFlag.RdpIntegration) &&
      deviceFamily === DeviceFamily.FIRE_TV &&
      placement === Placement.FEATURE_ROTATOR,
    [deviceFamily, placement],
  );

  const value = useMemo(
    () => ({
      isTestCampaignModalOpen,
      openTestCampaignModal,
      closeTestCampaignModal,
      isDevicePreviewAvailable,
    }),
    [
      isTestCampaignModalOpen,
      openTestCampaignModal,
      closeTestCampaignModal,
      isDevicePreviewAvailable,
    ],
  );

  return (
    <DevicePreviewNavigationContext.Provider value={value}>
      {children}
    </DevicePreviewNavigationContext.Provider>
  );
};
